import axios, { AxiosResponse } from 'axios';
import Cookies from 'universal-cookie';

import { CookiesKeys } from 'enums/cookies-keys.enum';
import { IVipPointsHistoryResponse } from 'models/vip/i-points-history.interface';
import {
  IVipPointsPurchaseInitiateResponse,
  IVipPointsPurchaseHistoryResponse,
  IVipPointsPurchaseCancelResponse,
  IVipPointsPurchaseConfirmResponse,
} from 'models/vip/i-points-purchase.interface';

export interface VipPointsBalanceResponse {
  balance: number;
}

export const getVipPointsBalance = (
  signal: AbortSignal,
  walletAddresses: string[] = []
): Promise<AxiosResponse<VipPointsBalanceResponse>> => {
  return axios.get(`/api/v1/vip/points/balance/`, {
    signal,
    params: walletAddresses.length > 0 ? { wallet_addresses: walletAddresses.join(',') } : undefined,
    headers: {
      'X-CSRFToken': new Cookies().get(CookiesKeys.csrftoken),
    },
  });
};

/**
 * Fetches VIP points transaction history with optional pagination.
 * @param signal - AbortSignal for request cancellation
 * @param page - Page number (optional, backend uses default if omitted)
 * @param pageSize - Number of items per page (optional, backend uses default if omitted)
 */
export const getVipPointsHistory = (
  signal: AbortSignal,
  page?: number,
  pageSize?: number
): Promise<AxiosResponse<IVipPointsHistoryResponse>> => {
  const params: Record<string, number> = {};
  if (page !== undefined) {
    params.page = page;
  }
  if (pageSize !== undefined) {
    params.page_size = pageSize;
  }

  return axios.get(`/api/v1/vip/points/history/`, {
    signal,
    params: Object.keys(params).length > 0 ? params : undefined,
    headers: {
      'X-CSRFToken': new Cookies().get(CookiesKeys.csrftoken),
    },
  });
};

/**
 * Initiates a VIP points purchase via RMV transfer.
 * @param signal - AbortSignal for request cancellation
 * @param rmvAmount - RMV token amount to spend
 */
export const initiateVipPointsPurchase = (
  signal: AbortSignal,
  rmvAmount: string
): Promise<AxiosResponse<IVipPointsPurchaseInitiateResponse>> => {
  return axios.post(
    `/api/v1/vip/points/purchase/`,
    { rmv_amount: rmvAmount },
    {
      signal,
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': new Cookies().get(CookiesKeys.csrftoken),
      },
    }
  );
};

export const confirmVipPointsPurchase = (
  signal: AbortSignal,
  purchaseId: number,
  txHash: string
): Promise<AxiosResponse<IVipPointsPurchaseConfirmResponse>> => {
  return axios.post(
    `/api/v1/vip/points/purchase/${purchaseId}/confirm/`,
    { tx_hash: txHash },
    {
      signal,
      headers: {
        'Content-Type': 'application/json',
        'X-CSRFToken': new Cookies().get(CookiesKeys.csrftoken),
      },
    }
  );
};

/**
 * Fetches VIP points purchase history with optional pagination.
 * @param signal - AbortSignal for request cancellation
 * @param page - Page number (optional, backend uses default if omitted)
 * @param pageSize - Number of items per page (optional, backend uses default if omitted)
 */
export const cancelVipPointsPurchase = (
  signal: AbortSignal,
  purchaseId: number
): Promise<AxiosResponse<IVipPointsPurchaseCancelResponse>> => {
  return axios.delete(`/api/v1/vip/points/purchase/${purchaseId}/`, {
    signal,
    headers: {
      'X-CSRFToken': new Cookies().get(CookiesKeys.csrftoken),
    },
  });
};

/**
 * Fetches VIP points purchase history with optional pagination.
 * @param signal - AbortSignal for request cancellation
 * @param page - Page number (optional, backend uses default if omitted)
 * @param pageSize - Number of items per page (optional, backend uses default if omitted)
 */
export const getVipPointsPurchaseHistory = (
  signal: AbortSignal,
  page?: number,
  pageSize?: number
): Promise<AxiosResponse<IVipPointsPurchaseHistoryResponse>> => {
  const params: Record<string, number> = {};
  if (page !== undefined) {
    params.page = page;
  }
  if (pageSize !== undefined) {
    params.page_size = pageSize;
  }

  return axios.get(`/api/v1/vip/points/purchase/`, {
    signal,
    params: Object.keys(params).length > 0 ? params : undefined,
    headers: {
      'X-CSRFToken': new Cookies().get(CookiesKeys.csrftoken),
    },
  });
};
